import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FileText, Search, Filter, Download, Calendar, DollarSign, CheckCircle, Clock,
  AlertCircle, CreditCard, Loader2, Edit3, Camera, Send, X, ChevronDown, ChevronUp, ImageIcon, Upload, Building2, MapPin, Truck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { clientPortalService } from '../../services/clientPortal.service';
import { getAdjustmentBorderClass, getAdjustmentBadgeClass } from '../../lib/statusHelpers';
import { isOverdue, canPay, getEffectiveStatus } from '../../lib/invoiceHelpers';
import { toast } from 'sonner';

const statusConfig = {
  open: { label: 'Open', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  ready_for_payment: { label: 'Ready to Pay', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  amend_for_review: { label: 'Adjustment Pending', color: 'bg-purple-100 text-purple-800', icon: Edit3 },
  amended_ready_for_payment: { label: 'Amended - Pay', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  processing_payment: { label: 'Processing', color: 'bg-yellow-100 text-yellow-800', icon: Loader2 },
  payment_processing: { label: 'ACH Processing', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  payment_failed: { label: 'Payment Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: X },
  void: { label: 'Void', color: 'bg-gray-100 text-gray-800', icon: X },
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const [searchParams] = useSearchParams();

  // Payment Methods state - now supports both cards and bank accounts
  const [cardMethods, setCardMethods] = useState([]);
  const [bankMethods, setBankMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null); // 'card' or 'bank'

  // Adjustment form state
  const [adjustmentForm, setAdjustmentForm] = useState({
    quantity_changes: [],
    reason: '',
    photo_urls: [],
  });
  const [submittingAdjustment, setSubmittingAdjustment] = useState(false);
  const [expandedAdjustment, setExpandedAdjustment] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const uploadPhoto = async (file) => {
    if (adjustmentForm.photo_urls.length >= 3) {
      toast.error('Maximum 3 photos allowed');
      return;
    }
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please use JPG, PNG, or WebP images');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const sigResp = await fetch(`${API_URL}/api/cloudinary/signature?folder=adjustments&resource_type=image`);
      if (!sigResp.ok) throw new Error('Failed to get upload signature');
      const sig = await sigResp.json();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      const uploadResp = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: 'POST', body: formData });
      if (!uploadResp.ok) throw new Error('Upload failed');
      const result = await uploadResp.json();
      const url = result.secure_url.replace('/upload/', '/upload/q_auto,f_auto/');

      setAdjustmentForm(prev => ({ ...prev, photo_urls: [...prev.photo_urls, url] }));
      toast.success('Photo uploaded');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index) => {
    setAdjustmentForm(prev => ({
      ...prev,
      photo_urls: prev.photo_urls.filter((_, i) => i !== index)
    }));
  };

  const loadInvoices = useCallback(async () => {
    try {
      const data = await clientPortalService.getInvoicesV2();
      setInvoices(data);
    } catch {
      // Fallback to old endpoint
      try {
        const data = await clientPortalService.getInvoices();
        setInvoices(data);
      } catch (err) {
        console.error('Error loading invoices:', err);
        toast.error('Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const status = await clientPortalService.getPaymentStatus(sessionId);
      if (status.payment_status === 'paid') {
        toast.success('Payment successful! Your invoice has been paid.');
        loadInvoices();
      } else if (status.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
      }
      window.history.replaceState({}, '', '/client/invoices');
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  }, [loadInvoices]);

  useEffect(() => {
    loadInvoices();
    const sessionId = searchParams.get('session_id');
    const invoiceId = searchParams.get('invoice_id');
    if (sessionId && invoiceId) {
      checkPaymentStatus(sessionId, invoiceId);
    }
  }, [searchParams, loadInvoices, checkPaymentStatus]);

  const handlePayNow = async (invoice) => {
    // Load all payment methods (cards and bank accounts)
    setSelectedInvoice(invoice);
    setLoadingPaymentMethods(true);
    setPaymentDialogOpen(true);
    setSelectedPaymentMethod(null);
    setSelectedPaymentType(null);
    
    try {
      const data = await clientPortalService.getAllPaymentMethods();
      setCardMethods(data.cards || []);
      setBankMethods(data.bank_accounts || []);
      
      // Pre-select default payment method
      const allMethods = [...(data.cards || []), ...(data.bank_accounts || [])];
      const defaultMethod = allMethods.find(m => m.is_default);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
        setSelectedPaymentType(defaultMethod.type === 'us_bank_account' ? 'bank' : 'card');
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      setCardMethods([]);
      setBankMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const handlePayWithSavedMethod = async () => {
    if (!selectedInvoice || !selectedPaymentMethod || !selectedPaymentType) return;
    
    setPaymentProcessing(selectedInvoice.id);
    try {
      let result;
      if (selectedPaymentType === 'bank') {
        result = await clientPortalService.payWithBankAccount(selectedInvoice.id, selectedPaymentMethod);
      } else {
        result = await clientPortalService.payWithSavedCard(selectedInvoice.id, selectedPaymentMethod);
      }
      
      if (result.success) {
        if (selectedPaymentType === 'bank') {
          toast.success('ACH payment initiated! Funds typically clear in 3-5 business days.');
        } else {
          toast.success('Payment successful!');
        }
        setPaymentDialogOpen(false);
        loadInvoices();
      } else {
        toast.error(result.message || 'Payment failed');
      }
    } catch (err) {
      toast.error('Payment failed. Please try again.');
    } finally {
      setPaymentProcessing(null);
    }
  };

  const handlePayWithStripe = async () => {
    if (!selectedInvoice) return;
    
    setPaymentProcessing(selectedInvoice.id);
    try {
      const { checkout_url } = await clientPortalService.createInvoiceCheckout(selectedInvoice.id);
      window.location.href = checkout_url;
    } catch {
      toast.error('Failed to initiate payment. Please try again.');
      setPaymentProcessing(null);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    setDownloadingPDF(invoice.id);
    try {
      const blob = await clientPortalService.downloadInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${invoice.invoice_number}`);
    } catch {
      toast.error('Failed to download invoice PDF');
    } finally {
      setDownloadingPDF(null);
    }
  };

  // Open adjustment dialog
  const handleRequestAdjustment = (invoice) => {
    const changes = (invoice.items || []).map(item => ({
      product_id: item.product_id || item.id || '',
      product_name: item.product_name || item.name || 'Unknown',
      original_qty: item.quantity || 0,
      new_qty: item.quantity || 0,
    }));
    setAdjustmentForm({ quantity_changes: changes, reason: '', photo_urls: [] });
    setSelectedInvoice(invoice);
    setAdjustmentDialogOpen(true);
  };

  const handleSubmitAdjustment = async () => {
    if (!adjustmentForm.reason || adjustmentForm.reason.length < 10) {
      toast.error('Please provide a reason (at least 10 characters)');
      return;
    }
    const hasChanges = adjustmentForm.quantity_changes.some(c => c.new_qty !== c.original_qty);
    if (!hasChanges) {
      toast.error('Please adjust at least one item quantity');
      return;
    }

    setSubmittingAdjustment(true);
    try {
      const changesOnly = adjustmentForm.quantity_changes.filter(c => c.new_qty !== c.original_qty);
      await clientPortalService.submitAdjustment(selectedInvoice.id, {
        quantity_changes: changesOnly,
        reason: adjustmentForm.reason,
        photo_urls: adjustmentForm.photo_urls,
      });
      toast.success('Adjustment request submitted for review');
      setAdjustmentDialogOpen(false);
      loadInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit adjustment');
    } finally {
      setSubmittingAdjustment(false);
    }
  };

  // Helpers — `isOverdue`, `canPay`, `getEffectiveStatus` are imported from
  // `lib/invoiceHelpers.js` so they can be unit-tested independently.

  const canRequestAdjustment = (invoice) => {
    return (
      invoice.has_guaranteed_returns &&
      invoice.adjustment_window_open &&
      !invoice.has_pending_adjustment &&
      ['ready_for_payment'].includes(invoice.status)
    );
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = (invoice.invoice_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (invoice.order_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      matchesStatus = getEffectiveStatus(invoice) === statusFilter;
    }
    return matchesSearch && matchesStatus;
  });

  const openInvoices = filteredInvoices.filter(i => !['paid', 'cancelled', 'void'].includes(i.status));
  const paidInvoices = filteredInvoices.filter(i => i.status === 'paid');

  const totalOpen = invoices.filter(i => !['paid', 'cancelled', 'void'].includes(i.status)).reduce((s, i) => s + (i.amount || 0), 0);
  const totalOverdue = invoices.filter(i => isOverdue(i)).reduce((s, i) => s + (i.amount || 0), 0);
  const totalSavings = invoices.reduce((s, i) => s + (i.total_savings || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  const InvoiceCard = ({ invoice }) => {
    const effectiveStatus = getEffectiveStatus(invoice);
    const config = statusConfig[effectiveStatus] || statusConfig.open;
    const StatusIcon = config.icon;
    const showAdjust = canRequestAdjustment(invoice);
    const showPay = canPay(invoice);
    // Only show payment failure if: payment_failed is true, has failure count, 
    // AND status is not 'paid' or actively processing a new payment
    const isActivelyProcessing = invoice.status === 'payment_processing' && !invoice.payment_failed;
    const hasPaymentFailure = invoice.payment_failed && invoice.payment_failure_count > 0 && invoice.status !== 'paid';

    return (
      <Card className={`hover:shadow-md transition-shadow ${hasPaymentFailure ? 'border-red-300' : ''}`}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${config.color.split(' ')[0]}`}>
                <StatusIcon className={`h-5 w-5 ${config.color.split(' ')[1]}`} />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{invoice.invoice_number}</p>
                {invoice.order_number && (
                  <p className="text-sm text-amber-600">Order: {invoice.order_number}</p>
                )}
                {invoice.location_name && (
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1.5" data-testid={`invoice-location-${invoice.id}`}>
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{invoice.location_name}</span>
                  </p>
                )}
                {invoice.delivery_date && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1" data-testid={`invoice-delivery-${invoice.id}`}>
                    <Truck className="h-3 w-3" />
                    <span>
                      {invoice.order_status === 'delivered' ? 'Delivered' : 'Delivery'}:{' '}
                      {new Date(invoice.delivery_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <Calendar className="h-3 w-3" />
                  <span>Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                </div>
                {invoice.has_guaranteed_returns && invoice.adjustment_window_open && (
                  <p className="text-xs text-purple-600 mt-1">
                    Adjustment window open until {new Date(invoice.adjustment_window_ends).toLocaleDateString()}
                  </p>
                )}
                {/* Payment Failure Warning */}
                {hasPaymentFailure && (
                  <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-red-700">Payment Failed</p>
                        <p className="text-xs text-red-600">{invoice.last_payment_error || 'Payment could not be processed'}</p>
                        <p className="text-xs text-red-500 mt-1">
                          Attempt {invoice.payment_failure_count} of 3
                          {invoice.payment_failure_count >= 3 && ' - Please update payment method'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Badge className={config.color}>{config.label}</Badge>
              {invoice.billing_timing === 'advance' && !invoice.revenue_recognized_at && invoice.order_status !== 'delivered' && (
                <Badge className="bg-slate-200 text-slate-700" data-testid={`advance-invoice-badge-${invoice.id}`}>
                  {invoice.status === 'paid' ? 'Paid in advance — not yet delivered' : 'Advance invoice — not yet delivered'}
                </Badge>
              )}
              {invoice.parent_invoice_number && (
                <Badge className="bg-amber-100 text-amber-700" data-testid={`supplemental-invoice-badge-${invoice.id}`}>
                  Additional invoice for {invoice.parent_invoice_number}
                </Badge>
              )}
              {invoice.credit_memos?.length > 0 && (
                <Badge className="bg-green-100 text-green-700" data-testid={`credit-issued-badge-${invoice.id}`}>
                  Credit issued
                </Badge>
              )}
              {hasPaymentFailure && <Badge className="bg-red-100 text-red-700">Payment Failed</Badge>}
              <p className="font-bold text-lg">${(invoice.amount || 0).toFixed(2)}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setSelectedInvoice(invoice); setInvoiceDialogOpen(true); }}>
                  View
                </Button>
                {showAdjust && (
                  <Button size="sm" variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50"
                    onClick={() => handleRequestAdjustment(invoice)}>
                    <Edit3 className="h-4 w-4 mr-1" /> Adjust
                  </Button>
                )}
                {showPay && (
                  <Button size="sm" className={hasPaymentFailure ? "bg-red-500 hover:bg-red-600" : "bg-amber-500 hover:bg-amber-600"}
                    onClick={() => handlePayNow(invoice)} disabled={paymentProcessing === invoice.id}>
                    {paymentProcessing === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1" />{hasPaymentFailure ? 'Retry' : 'Pay'}</>}
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => handleDownloadPDF(invoice)} disabled={downloadingPDF === invoice.id}>
                  {downloadingPDF === invoice.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Invoices & Payments</h1>
        <p className="text-sm text-gray-600 mt-1">View, adjust, and pay your invoices</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-yellow-100 rounded-lg"><DollarSign className="h-5 w-5 text-yellow-600" /></div><div><p className="text-sm text-gray-500">Outstanding</p><p className="text-xl font-bold">${totalOpen.toFixed(2)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-red-100 rounded-lg"><AlertCircle className="h-5 w-5 text-red-600" /></div><div><p className="text-sm text-gray-500">Overdue</p><p className="text-xl font-bold text-red-600">${totalOverdue.toFixed(2)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-purple-100 rounded-lg"><DollarSign className="h-5 w-5 text-purple-600" /></div><div><p className="text-sm text-gray-500">Total Savings</p><p className="text-xl font-bold text-purple-600">${totalSavings.toFixed(2)}</p></div></div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div><div><p className="text-sm text-gray-500">Total Invoices</p><p className="text-xl font-bold">{invoices.length}</p></div></div></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search invoices..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48"><Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(statusConfig).map(([key, c]) => <SelectItem key={key} value={key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="outstanding" className="space-y-4">
        <TabsList>
          <TabsTrigger value="outstanding">Outstanding ({openInvoices.length})</TabsTrigger>
          <TabsTrigger value="paid">Paid ({paidInvoices.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="outstanding" className="space-y-3">
          {openInvoices.length === 0 ? (
            <div className="text-center py-12"><CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" /><p className="text-gray-500">All caught up!</p></div>
          ) : openInvoices.map(inv => <InvoiceCard key={inv.id} invoice={inv} />)}
        </TabsContent>
        <TabsContent value="paid" className="space-y-3">
          {paidInvoices.length === 0 ? (
            <div className="text-center py-12"><FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">No paid invoices yet</p></div>
          ) : paidInvoices.map(inv => <InvoiceCard key={inv.id} invoice={inv} />)}
        </TabsContent>
      </Tabs>

      {/* Invoice Detail Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedInvoice && (
            <>
              <DialogHeader>
                <DialogTitle>Invoice {selectedInvoice.invoice_number}</DialogTitle>
                <DialogDescription>
                  {selectedInvoice.client_type_name && `Client Type: ${selectedInvoice.client_type_name}`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-gray-500 uppercase">Status</p><Badge className={statusConfig[getEffectiveStatus(selectedInvoice)]?.color}>{statusConfig[getEffectiveStatus(selectedInvoice)]?.label}</Badge></div>
                  <div><p className="text-xs text-gray-500 uppercase">Amount</p><p className="font-bold text-xl">${(selectedInvoice.amount || 0).toFixed(2)}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase">Invoice Date</p><p className="font-medium">{new Date(selectedInvoice.invoice_date || selectedInvoice.created_at).toLocaleDateString()}</p></div>
                  <div><p className="text-xs text-gray-500 uppercase">Due Date</p><p className={`font-medium ${getEffectiveStatus(selectedInvoice) === 'overdue' ? 'text-red-600' : ''}`}>{new Date(selectedInvoice.due_date).toLocaleDateString()}</p></div>
                  {selectedInvoice.order_date && <div data-testid="client-invoice-order-date"><p className="text-xs text-gray-500 uppercase">Order Date</p><p className="font-medium">{new Date(selectedInvoice.order_date).toLocaleDateString()}</p></div>}
                  {selectedInvoice.delivery_date && <div data-testid="client-invoice-delivery-date"><p className="text-xs text-gray-500 uppercase">Delivery Date</p><p className="font-medium">{new Date(selectedInvoice.delivery_date).toLocaleDateString()}</p></div>}
                  {selectedInvoice.order_number && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Order</p>
                    <p className="text-amber-600 font-medium">{selectedInvoice.order_number}</p>
                    {selectedInvoice.order_id && (
                      <Link
                        to={`/client/orders/${selectedInvoice.order_id}`}
                        className="text-xs text-amber-600 hover:text-amber-700 underline inline-flex items-center gap-1 mt-0.5"
                        data-testid="client-invoice-proof-link"
                      >
                        <Camera className="h-3 w-3" /> View delivery proof
                      </Link>
                    )}
                  </div>
                )}
                  {selectedInvoice.total_savings > 0 && <div><p className="text-xs text-gray-500 uppercase">Savings</p><p className="font-medium text-green-600">${selectedInvoice.total_savings.toFixed(2)}</p></div>}
                </div>

                {/* Pickup adjustment notice */}
                {selectedInvoice.has_pickup_adjustments && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="client-pickup-adjustment-notice">
                    Quantities were adjusted at pickup; this invoice reflects delivered quantities.
                  </div>
                )}

                {/* Credits raised against this invoice */}
                {selectedInvoice.credit_memos?.length > 0 && (
                  <div className="space-y-2" data-testid="client-credit-memos">
                    {selectedInvoice.credit_memos.map((cm) => {
                      const fromChange = cm.category_label === 'Approved Order Change';
                      return (
                        <div key={cm.id} className="p-3 rounded-lg bg-green-50 border border-green-200">
                          <p className="text-sm font-medium text-green-800">
                            Credit issued: ${Math.abs(cm.credit_amount || 0).toFixed(2)}
                            <span className="ml-2 text-xs font-normal text-green-700">{cm.credit_memo_number}</span>
                          </p>
                          <p className="text-xs text-green-700 mt-0.5">
                            {fromChange
                              ? 'Your approved order change reduced this order.'
                              : 'Delivered quantities were lower than invoiced.'}
                          </p>
                          {cm.lines?.length > 0 && (
                            <div className="mt-2 space-y-0.5">
                              {cm.lines.map((ln) => (
                                <p key={`${cm.id}-${ln.product_id}`} className="text-xs text-green-800">
                                  {ln.product_name}: {fromChange ? 'Ordered' : 'Invoiced'} {ln.invoiced_qty} → {fromChange ? 'Requested' : 'Delivered'} {ln.delivered_qty}
                                  {ln.reason ? ` — ${ln.reason}` : ''}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Items */}
                {selectedInvoice.items?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Items</p>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50"><tr><th className="text-left p-2">Product</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Subtotal</th></tr></thead>
                        <tbody>{selectedInvoice.items.map((item) => (
                          <tr key={item.product_name || item.name} className="border-t">
                            <td className="p-2">
                              {item.product_name || item.name}
                              {item.pickup_adjusted && (
                                <div className="mt-0.5" data-testid={`client-pickup-adjusted-${item.product_id || item.product_name}`}>
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">Adjusted at pickup</Badge>
                                  <span className="text-xs text-amber-700 ml-1.5">Ordered {item.ordered_qty} → Delivered {item.delivered_qty}</span>
                                </div>
                              )}
                            </td>
                            <td className="text-right p-2">{item.quantity}{item.original_quantity && item.original_quantity !== item.quantity && <span className="text-xs text-gray-400 line-through ml-1">{item.original_quantity}</span>}</td>
                            <td className="text-right p-2">${(item.subtotal || 0).toFixed(2)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Adjustment History */}
                {selectedInvoice.adjustment_requests?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Adjustment History</p>
                    <div className="space-y-2">
                      {selectedInvoice.adjustment_requests.map((adj, i) => (
                        <div key={adj.id || `adj-${i}`} className={`p-3 rounded-lg border-l-4 ${getAdjustmentBorderClass(adj.status)}`}>
                          <div className="flex justify-between items-start cursor-pointer" onClick={() => setExpandedAdjustment(expandedAdjustment === i ? null : i)}>
                            <div>
                              <Badge className={getAdjustmentBadgeClass(adj.status)}>{adj.status}</Badge>
                              <p className="text-sm mt-1">${adj.original_amount?.toFixed(2)} → ${adj.requested_amount?.toFixed(2)}</p>
                            </div>
                            {expandedAdjustment === i ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </div>
                          {expandedAdjustment === i && (
                            <div className="mt-2 pt-2 border-t text-sm space-y-1">
                              <p><span className="text-gray-500">Reason:</span> {adj.reason}</p>
                              <p><span className="text-gray-500">Date:</span> {new Date(adj.requested_at).toLocaleString()}</p>
                              {adj.rejection_reason && <p className="text-red-600"><span className="text-gray-500">Rejection:</span> {adj.rejection_reason}</p>}
                              {adj.photo_urls?.length > 0 && (
                                <div className="flex gap-2 mt-2">{adj.photo_urls.map((url, j) => <img key={`${i}-photo-${url}-${j}`} src={url} alt="Proof" className="h-16 w-16 object-cover rounded" />)}</div>
                              )}
                              {adj.quantity_changes?.length > 0 && (
                                <div className="mt-1">{adj.quantity_changes.map((c, j) => <p key={`${i}-qty-${c.product_id || c.product_name}-${j}`} className="text-xs">{c.product_name}: {c.original_qty} → {c.new_qty}</p>)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button className="flex-1" variant="outline" onClick={() => handleDownloadPDF(selectedInvoice)} disabled={downloadingPDF === selectedInvoice.id}>
                    <Download className="h-4 w-4 mr-2" />{downloadingPDF === selectedInvoice.id ? 'Downloading...' : 'Download PDF'}
                  </Button>
                  {canRequestAdjustment(selectedInvoice) && (
                    <Button className="flex-1 border-purple-300 text-purple-600 hover:bg-purple-50" variant="outline" onClick={() => { setInvoiceDialogOpen(false); handleRequestAdjustment(selectedInvoice); }}>
                      <Edit3 className="h-4 w-4 mr-2" />Request Adjustment
                    </Button>
                  )}
                  {canPay(selectedInvoice) && (
                    <Button className="flex-1 bg-amber-500 hover:bg-amber-600" onClick={() => handlePayNow(selectedInvoice)} disabled={paymentProcessing === selectedInvoice.id}>
                      <CreditCard className="h-4 w-4 mr-2" />{paymentProcessing === selectedInvoice.id ? 'Processing...' : 'Pay Now'}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjustment Request Dialog */}
      <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit3 className="h-5 w-5 text-purple-500" />Request Invoice Adjustment</DialogTitle>
            <DialogDescription>
              Adjust quantities for unsold items. This will be reviewed by the admin team.
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 pt-2">
              <div className="p-3 bg-purple-50 rounded-lg text-sm">
                <p className="font-medium text-purple-800">Invoice: {selectedInvoice.invoice_number}</p>
                <p className="text-purple-600">Current Amount: ${(selectedInvoice.amount || 0).toFixed(2)}</p>
                {selectedInvoice.adjustment_window_ends && (
                  <p className="text-purple-600">Window closes: {new Date(selectedInvoice.adjustment_window_ends).toLocaleDateString()}</p>
                )}
              </div>

              {/* Quantity Changes */}
              <div>
                <Label className="text-sm font-medium">Adjust Quantities</Label>
                <div className="space-y-2 mt-2">
                  {adjustmentForm.quantity_changes.map((item, i) => (
                    <div key={item.product_name || `qty-${i}`} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.product_name}</p>
                        <p className="text-xs text-gray-500">Original: {item.original_qty}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" className="h-8 w-8"
                          onClick={() => {
                            const updated = [...adjustmentForm.quantity_changes];
                            updated[i] = { ...updated[i], new_qty: Math.max(0, updated[i].new_qty - 1) };
                            setAdjustmentForm(prev => ({ ...prev, quantity_changes: updated }));
                          }}>-</Button>
                        <Input type="number" className="w-16 h-8 text-center" min={0} max={item.original_qty}
                          value={item.new_qty}
                          onChange={(e) => {
                            const val = Math.min(Math.max(0, parseInt(e.target.value) || 0), item.original_qty);
                            const updated = [...adjustmentForm.quantity_changes];
                            updated[i] = { ...updated[i], new_qty: val };
                            setAdjustmentForm(prev => ({ ...prev, quantity_changes: updated }));
                          }} />
                        <Button variant="outline" size="icon" className="h-8 w-8"
                          onClick={() => {
                            const updated = [...adjustmentForm.quantity_changes];
                            updated[i] = { ...updated[i], new_qty: Math.min(updated[i].original_qty, updated[i].new_qty + 1) };
                            setAdjustmentForm(prev => ({ ...prev, quantity_changes: updated }));
                          }}>+</Button>
                      </div>
                      {item.new_qty !== item.original_qty && (
                        <Badge className="bg-red-100 text-red-700 text-xs">-{item.original_qty - item.new_qty}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label>Reason for Adjustment *</Label>
                <Textarea placeholder="Explain why items were unsold (min 10 characters)..." rows={3}
                  value={adjustmentForm.reason}
                  onChange={(e) => setAdjustmentForm(prev => ({ ...prev, reason: e.target.value }))} className="mt-1" />
              </div>

              {/* Photo Upload */}
              <div>
                <Label>Photo Evidence (optional, up to 3)</Label>
                {adjustmentForm.photo_urls.length > 0 && (
                  <div className="flex gap-2 mt-2 mb-2">
                    {adjustmentForm.photo_urls.map((url, i) => (
                      <div key={url} className="relative group">
                        <img src={url} alt={`Evidence ${i + 1}`} className="h-20 w-20 object-cover rounded-lg border" />
                        <button onClick={() => removePhoto(i)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {adjustmentForm.photo_urls.length < 3 && (
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors mt-1
                      ${dragOver ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50/50'}`}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) uploadPhoto(f); }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                    onClick={() => document.getElementById('adj-photo-input').click()}
                  >
                    <input id="adj-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ''; }} />
                    {uploadingPhoto ? (
                      <div className="flex flex-col items-center py-2">
                        <Loader2 className="h-6 w-6 text-purple-500 animate-spin" />
                        <p className="text-sm text-gray-500 mt-1">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-1">
                        <Upload className="h-6 w-6 text-gray-400" />
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-medium text-purple-600">Click to upload</span> or drag & drop
                        </p>
                        <p className="text-xs text-gray-400">JPG, PNG, or WebP (max 10MB) — {3 - adjustmentForm.photo_urls.length} remaining</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setAdjustmentDialogOpen(false)}>Cancel</Button>
                <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={handleSubmitAdjustment} disabled={submittingAdjustment}>
                  {submittingAdjustment ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  {submittingAdjustment ? 'Submitting...' : 'Submit Adjustment'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Method Selection Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-amber-500" />
              Pay Invoice
            </DialogTitle>
            <DialogDescription>
              Choose how you'd like to pay
            </DialogDescription>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 pt-2">
              {/* Invoice Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <p className="font-medium">{selectedInvoice.invoice_number}</p>
                  <p className="text-xl font-bold text-amber-600">${(selectedInvoice.amount || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Loading State */}
              {loadingPaymentMethods ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
                </div>
              ) : (cardMethods.length > 0 || bankMethods.length > 0) ? (
                <div className="space-y-4">
                  {/* Saved Cards Section */}
                  {cardMethods.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" /> Credit/Debit Cards
                      </p>
                      {cardMethods.map(method => (
                        <div
                          key={method.id}
                          onClick={() => {
                            setSelectedPaymentMethod(method.id);
                            setSelectedPaymentType('card');
                          }}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors flex items-center gap-3
                            ${selectedPaymentMethod === method.id 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                            ${selectedPaymentMethod === method.id ? 'border-amber-500' : 'border-gray-300'}`}>
                            {selectedPaymentMethod === method.id && (
                              <div className="w-3 h-3 rounded-full bg-amber-500" />
                            )}
                          </div>
                          <CreditCard className="h-5 w-5 text-blue-500" />
                          <div className="flex-1">
                            <p className="font-medium">{method.brand} •••• {method.last4}</p>
                            <p className="text-xs text-gray-500">Expires {method.exp_month}/{method.exp_year}</p>
                          </div>
                          {method.is_default && (
                            <span className="text-xs text-amber-600 font-medium">Default</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Bank Accounts Section */}
                  {bankMethods.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Building2 className="h-4 w-4" /> Bank Accounts (ACH)
                      </p>
                      {bankMethods.map(method => (
                        <div
                          key={method.id}
                          onClick={() => {
                            setSelectedPaymentMethod(method.id);
                            setSelectedPaymentType('bank');
                          }}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors flex items-center gap-3
                            ${selectedPaymentMethod === method.id 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-gray-200 hover:border-gray-300'}`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                            ${selectedPaymentMethod === method.id ? 'border-amber-500' : 'border-gray-300'}`}>
                            {selectedPaymentMethod === method.id && (
                              <div className="w-3 h-3 rounded-full bg-amber-500" />
                            )}
                          </div>
                          <Building2 className="h-5 w-5 text-green-600" />
                          <div className="flex-1">
                            <p className="font-medium">{method.bank_name} •••• {method.last4}</p>
                            <p className="text-xs text-gray-500">{method.account_type} account</p>
                          </div>
                          <Badge variant="outline" className="text-green-600 border-green-300 text-xs">ACH</Badge>
                          {method.is_default && (
                            <span className="text-xs text-amber-600 font-medium">Default</span>
                          )}
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 italic">
                        ACH payments typically take 3-5 business days to process
                      </p>
                    </div>
                  )}
                  
                  {/* Pay Button */}
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 mt-3"
                    onClick={handlePayWithSavedMethod}
                    disabled={!selectedPaymentMethod || paymentProcessing === selectedInvoice.id}
                  >
                    {paymentProcessing === selectedInvoice.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : selectedPaymentType === 'bank' ? (
                      <Building2 className="h-4 w-4 mr-2" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    Pay ${(selectedInvoice.amount || 0).toFixed(2)}
                    {selectedPaymentType === 'bank' && ' via ACH'}
                  </Button>
                </div>
              ) : null}

              {/* Divider */}
              {(cardMethods.length > 0 || bankMethods.length > 0) && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or</span>
                  </div>
                </div>
              )}

              {/* Pay with Stripe Checkout */}
              <Button
                variant={(cardMethods.length > 0 || bankMethods.length > 0) ? "outline" : "default"}
                className={(cardMethods.length === 0 && bankMethods.length === 0) ? "w-full bg-amber-500 hover:bg-amber-600" : "w-full"}
                onClick={handlePayWithStripe}
                disabled={paymentProcessing === selectedInvoice.id}
              >
                {paymentProcessing === selectedInvoice.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                {(cardMethods.length > 0 || bankMethods.length > 0) ? 'Pay with new card' : 'Pay Now'}
              </Button>

              {(cardMethods.length === 0 && bankMethods.length === 0) && (
                <p className="text-xs text-center text-gray-500">
                  Save time by adding a payment method in <a href="/client/payment-methods" className="text-amber-600 hover:underline">Payment Methods</a>
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
